<template>
    <CompassModal
        v-model:visible="isVisible"
        :title="modalTitle"
        :primaryAction="primaryAction"
        :secondaryActions="secondaryActions"
        closeOnClickOverlay
        @closed="isVisible = false"
    >
        <div class="setup-provider-modal">
            <div class="setup-provider-modal__logos">
                <img :src="provider.logo" :alt="provider.name" class="setup-provider-modal__logo" />
                <div class="setup-provider-modal__arrow">
                    <CompassIcon icon="arrow-right" :size="CompassEnums.IconSize.SMALL" />
                    <CompassIcon icon="arrow-left" :size="CompassEnums.IconSize.SMALL" />
                </div>
                <img
                    src="/ressources/logged/img/icon/svg/sellsyCloud.svg"
                    alt="Sellsy"
                    class="setup-provider-modal__logo"
                />
            </div>

            <p class="setup-provider-modal__subtitle typographies-paragraph-medium-regular">
                {{ subtitle }}
            </p>

            <div class="setup-provider-modal__info-cards">
                <QuickpaySettingsSetupProviderInfoCard
                    icon="subscription"
                    :title="translations.paymentMethodsLabel"
                >
                    <CompassPill
                        v-for="(method, index) in paymentMethods"
                        :key="index"
                        :icon="method.icon"
                        :label="method.label"
                        :variant="CompassEnums.PillVariant.PASTEL"
                        :color="CompassEnums.PillColor.PURPLE"
                        :size="CompassEnums.PillSize.SMALL"
                    />
                </QuickpaySettingsSetupProviderInfoCard>
                <QuickpaySettingsSetupProviderInfoCard
                    icon="moneybag-move-back"
                    :title="translations.feesLabel"
                >
                    <div class="setup-provider-modal__fees-content">
                        <span
                            class="setup-provider-modal__info-card-value typographies-paragraph-x-large-regular"
                        >
                            {{ feesAmount }}
                        </span>
                        <CompassPlainButton
                            v-if="provider.setupModal.fees.pricingUrl"
                            :label="translations.pricingLink"
                            :size="CompassEnums.PlainButtonSize.SMALL"
                            :variant="CompassEnums.PlainButtonVariantType.NEUTRAL"
                            leftIcon="external-link"
                            @click="openPricingUrl"
                        />
                    </div>
                </QuickpaySettingsSetupProviderInfoCard>
                <QuickpaySettingsSetupProviderInfoCard
                    icon="clock-up"
                    :title="translations.payoutLabel"
                >
                    <div class="setup-provider-modal__payout-text">
                        <span
                            class="setup-provider-modal__info-card-value typographies-paragraph-x-large-regular"
                        >
                            {{ payoutDelay }}
                        </span>
                        <span
                            class="setup-provider-modal__info-card-footnote typographies-paragraph-small-regular"
                        >
                            {{ payoutSubtitle }}
                        </span>
                    </div>
                </QuickpaySettingsSetupProviderInfoCard>
            </div>
            <CompassCallout
                :title="advantagesTitle"
                icon="success-circle-fill"
                :items="advantageItems"
            />
            <QuickpaySettingsFaq v-if="faqItems.length" :items="faqItems" />
        </div>
    </CompassModal>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { Translator } from 'js/shims-vue';
import QuickpaySettingsFaq from 'vcomponents/quickpay/settings/QuickpaySettingsFaq.vue';
import QuickpaySettingsSetupProviderInfoCard from 'vcomponents/quickpay/settings/QuickpaySettingsSetupProviderInfoCard.vue';

interface PaymentMethod {
    icon: string;
    labelKey: string;
}

interface FaqItem {
    questionKey: string;
    answerKey: string;
}

interface ProviderSetupModal {
    subtitleKey: string;
    paymentMethods: PaymentMethod[];
    fees: { amountKey: string; pricingUrl?: string };
    payout: { delayKey: string; subtitleKey: string };
    advantages: string[];
    learnMoreUrl?: string;
    faq?: FaqItem[];
}

interface Provider {
    id: string;
    name: string;
    logo: string;
    setupModal: ProviderSetupModal;
}

interface Props {
    provider: Provider;
    isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    isLoading: false,
});

const emit = defineEmits(['connectProvider']);

const isVisible = defineModel('visible', { type: Boolean, default: false });

const $t = inject('$t') as Translator;

const modalTitle = computed((): string =>
    $t.__('quickpay.settings.setupModal.title', { providerName: props.provider.name }),
);

const primaryAction = computed((): { label: string; loading: boolean; action: () => void } => ({
    label: $t.__('quickpay.settings.setupModal.common.connectAccount', {
        providerName: props.provider.name,
    }),
    loading: props.isLoading,
    action: () => emit('connectProvider'),
}));

const secondaryActions = computed((): { label: string; leftIcon: string; action: () => void }[] => {
    if (!props.provider.setupModal.learnMoreUrl) {
        return [];
    }
    return [
        {
            label: $t.__('common.labels.learn_more'),
            leftIcon: 'external-link',
            action: () => {
                window.open(
                    props.provider.setupModal.learnMoreUrl,
                    '_blank',
                    'noopener,noreferrer',
                );
            },
        },
    ];
});

const advantagesTitle = computed((): string =>
    $t.__('quickpay.settings.setupModal.common.advantagesTitle', {
        providerName: props.provider.name,
    }),
);

const advantageItems = computed((): string[] =>
    props.provider.setupModal.advantages.map((key) => $t.__(key)),
);

const subtitle = computed((): string =>
    $t.__(props.provider.setupModal.subtitleKey),
);

const paymentMethods = computed((): { icon: string; label: string }[] =>
    props.provider.setupModal.paymentMethods.map((method) => ({
        icon: method.icon,
        label: $t.__(method.labelKey),
    })),
);

const feesAmount = computed((): string =>
    $t.__(props.provider.setupModal.fees.amountKey),
);

const payoutDelay = computed((): string =>
    $t.__(props.provider.setupModal.payout.delayKey),
);

const payoutSubtitle = computed((): string =>
    $t.__(props.provider.setupModal.payout.subtitleKey),
);

const faqItems = computed((): FaqItem[] => props.provider.setupModal.faq ?? []);

const translations = {
    paymentMethodsLabel: $t.__('common.labels.payment_method'),
    feesLabel: $t.__('common.labels.fees'),
    payoutLabel: $t.__('common.labels.payout'),
    pricingLink: $t.__('quickpay.settings.setupModal.common.pricingLink'),
};

const openPricingUrl = (): void => {
    window.open(props.provider.setupModal.fees.pricingUrl, '_blank', 'noopener,noreferrer');
};
</script>

<style lang="scss" scoped>
.setup-provider-modal {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
    &__logos {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-2);
    }
    &__logo {
        width: 48px;
        height: 48px;
        object-fit: contain;
        border-radius: var(--spacing-1);
    }
    &__arrow {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0;
        color: var(--colors-common-text-subtle-default);
    }
    &__subtitle {
        text-align: center;
        margin: 0;
        color: var(--colors-common-text-subtle-default);
    }
    &__info-cards {
        display: flex;
        gap: var(--spacing-2);
        width: 100%;
    }
    &__fees-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-2);
    }
    &__payout-text {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }
    &__info-card-value {
        color: var(--colors-common-text-standard-default);
    }
    &__info-card-footnote {
        color: var(--colors-common-text-standard-caption);
    }
}
</style>
